import type { Signal } from '@preact/signals-react';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import type { MutableRefObject } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OdinDataRetrieval } from '../../common/data-retrieval/data-retrieval-interfaces';
import type { OdinKey } from '../../common/types/odin-key.type';
import type { ContinuousScrollerRef } from '../continuous-scroller/continuous-scroller.component';
import ContinuousScroller from '../continuous-scroller/continuous-scroller.component';
import { filterData, sortData } from '../continuous-scroller/continuous-scroller-functions';
import type {
  ContinuousScrollerFilter,
  ContinuousScrollerSort,
} from '../continuous-scroller/continuous-scroller-types';
import { DataSearchLocation } from '../enums';
import type { JsonData } from '../generic-types';
import OdinIcon from '../odin-icon/odin-icon.component';
import type { Api } from './api/api';
import type { DataGridCellRenderer } from './data-grid-custom-column.interface';
import type { RowTemplateType } from './data-grid-data-row.component';
import DataGridDataRow from './data-grid-data-row.component';
import { getColumnMaxWidth, getColumnMinWidth, getIdFieldColumn } from './data-grid-functions';
import type { DataGridColumnSettings, DataGridRowIndicator } from './data-grid-settings.interface';
import { computePreferredWidth } from './functions/data-grid-auto-sizing-functions';
import { adjustSignalColumnsToTakeAvailableSpace } from './functions/data-grid-signal-functions';
export interface DataGridDataProps {
  columns: Signal<Array<DataGridColumnSettings>>;
  selectedRows: Signal<JsonData>;
  highlightedRows: Signal<JsonData>;
  staticData: Signal<JsonData>;
  pageSize: Signal<number>;
  optionsContainerRef: (element: HTMLElement | null) => void;
  data: JsonData | OdinDataRetrieval;
  customData?: JsonData;
  rowIndicator?: DataGridRowIndicator;
  scrollerElement: Signal<HTMLElement | null>;
  filterSignal: Signal<Array<ContinuousScrollerFilter>>;
  sortSignal: Signal<Array<ContinuousScrollerSort>>;
  sortFunction?: (data: JsonData, sorts: Array<ContinuousScrollerSort>) => JsonData;
  filterLocation?: DataSearchLocation;
  minimumPageCount?: number;
  stopRequestsOnNoData?: boolean;
  rowIsClickable?: boolean;
  api: Api;
  components?: Record<string, DataGridCellRenderer>;
  rowTemplate?: RowTemplateType;
  allowCellScrollBar?: boolean;
  autoSizeColumns?: boolean;
  // The measured pixel width of the row indicator column so commitMeasurements can subtract it
  indicatorRowWidth: Signal<number>;
  columnWidthAdjustedRef: MutableRefObject<boolean>;
  autoSizingInProgressRef: MutableRefObject<boolean>;
  cancelPendingResize: () => void;
  isPaginated?: boolean;
}

export default function DataGridData({
  columns,
  selectedRows,
  highlightedRows,
  staticData,
  pageSize,
  optionsContainerRef,
  data,
  customData,
  rowIndicator,
  scrollerElement,
  filterSignal,
  sortSignal,
  sortFunction = sortData,
  filterLocation = DataSearchLocation.Local,
  minimumPageCount,
  stopRequestsOnNoData,
  rowIsClickable = true,
  api,
  components,
  rowTemplate,
  allowCellScrollBar = false,
  autoSizeColumns = false,
  indicatorRowWidth,
  columnWidthAdjustedRef,
  autoSizingInProgressRef,
  cancelPendingResize,
  isPaginated = true,
}: DataGridDataProps) {
  const [finalData, setFinalData] = useState<JsonData>();
  const [useDataRetriever, setUseDataRetriever] = useState(false);
  const [columnChangedTrigger, setColumnsChangedTrigger] = useState(false);
  const [idFieldName, setIdFieldName] = useState<string>();
  const [scrollingElement, setScrollingElement] = useState<HTMLElement | null>(null);

  const dataRetriever = useRef<OdinDataRetrieval | undefined>(undefined);
  const previousColumns = useRef<Array<DataGridColumnSettings> | undefined>(undefined);
  const columnWidthMap = useRef<Map<OdinKey, number>>(new Map());

  // Auto-sizing measurement state
  const uniqueRowIds = useRef(new Set<string>());
  const measurementTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isCommitting = useRef(false);
  const hasCommittedAutoSizeRef = useRef(false);
  const widthSamplesRef = useRef<Map<string, number[]>>(new Map());
  const MAX_SAMPLE_ROWS = 20;
  const PREFERRED_WIDTH_STRATEGY: 'p90' | 'max' = 'p90';
  const MEASUREMENT_DEBOUNCE_MS = 75;

  // Watch for significant column structure changes and reset auto-sizing
  const prevColumnsCountRef = useRef(0);
  useSignalEffect(() => {
    const currentCount = columns.value.length;
    const prevCount = prevColumnsCountRef.current;

    // Only reset auto-sizing for significant structural changes
    if (currentCount !== prevCount && prevCount > 0 && autoSizeColumns) {
      if (!isCommitting.current) {
        resetAutoSizeSession();
      }
    }

    prevColumnsCountRef.current = currentCount;
  });

  const { t } = useTranslation();

  const resetAutoSizeSession = useCallback(() => {
    // Allow autosize to run again for the next session
    hasCommittedAutoSizeRef.current = false;
    columnWidthAdjustedRef.current = false;

    // Clear measurement buffers
    columnWidthMap.current.clear();
    uniqueRowIds.current.clear();
    widthSamplesRef.current.clear();

    // Cancel any scheduled commit
    if (measurementTimer.current) {
      clearTimeout(measurementTimer.current);
      measurementTimer.current = null;
    }

    isCommitting.current = false;
  }, []);

  // Commit measured widths to columns
  const commitMeasurements = useCallback(() => {
    if (hasCommittedAutoSizeRef.current || isCommitting.current || columnWidthMap.current.size === 0) {
      return;
    }

    // Prevent re-entry immediately
    isCommitting.current = true;
    hasCommittedAutoSizeRef.current = true;

    // signal update needs new refs
    const currentColumns = columns.value;
    const updatedColumns = currentColumns.map((col) => ({ ...col }));

    try {
      // Apply preferred widths with clamping
      for (const [columnId] of columnWidthMap.current.entries()) {
        const columnIdString = String(columnId);
        const column = updatedColumns.find((col) => String(col.id) === columnIdString);

        if (!column) continue;

        const minWidth = getColumnMinWidth(column);
        const maxWidth = getColumnMaxWidth(column);

        const samples = widthSamplesRef.current.get(columnIdString) ?? [];
        const preferredWidth = computePreferredWidth(samples, minWidth, PREFERRED_WIDTH_STRATEGY);
        const finalWidth = Math.max(minWidth, Math.min(preferredWidth, maxWidth));

        column.width = finalWidth;
      }

      columnWidthMap.current.clear();
      uniqueRowIds.current.clear();
      widthSamplesRef.current.clear();

      if (measurementTimer.current) {
        clearTimeout(measurementTimer.current);
        measurementTimer.current = null;
      }
      // Mark that we've adjusted column widths
      columnWidthAdjustedRef.current = true;

      // Cancel any pending resize calls and set flag to prevent new ones
      cancelPendingResize();
      autoSizingInProgressRef.current = true;

      // Defer signal update to break synchronous re-render cycle
      setTimeout(() => {
        try {
          // Merge the live visibility state back into our snapshot before writing it back,
          // so that stale visibility from the snapshot never overwrites live user changes.
          const liveColumns = columns.value;
          for (const updated of updatedColumns) {
            const live = liveColumns.find((c) => c.id === updated.id);
            if (live) {
              updated.visible = live.visible;
            }
          }

          columns.value = updatedColumns;

          // Subtract the indicator column width from clientWidth instead of SETTINGS_MENU_WIDTH_PX (a hardcoded 45px)
          // Using SETTINGS_MENU_WIDTH_PX  caused the columns to be slightly too wide producing a persistent horizontal scrollbar.
          const fullWidth = api?.data?.getClientWidth?.() ?? 0;
          const indicatorWidth = indicatorRowWidth.value ?? 0;
          const availableWidth = fullWidth - indicatorWidth;

          if (fullWidth > 0 && autoSizeColumns) {
            adjustSignalColumnsToTakeAvailableSpace(availableWidth, columns);
          }
        } catch (e) {
          hasCommittedAutoSizeRef.current = false;
          throw e;
        } finally {
          // Re-enable normal resize handling once the auto-size commit has fully finished.
          isCommitting.current = false;
          autoSizingInProgressRef.current = false;
        }
      }, 0);
    } catch (e) {
      // If something throws before the deferred update runs, allow retry
      isCommitting.current = false;
      hasCommittedAutoSizeRef.current = false;
      throw e;
    }
  }, [columns, api, autoSizeColumns, cancelPendingResize, indicatorRowWidth]);

  const isSizingActive = autoSizeColumns && !hasCommittedAutoSizeRef.current;

  const handleCellMeasured = useCallback(
    (columnId: string, width: number, rowId?: string) => {
      if (hasCommittedAutoSizeRef.current || width <= 0 || !rowId) return;

      const isNewRow = !uniqueRowIds.current.has(rowId);
      if (isNewRow) {
        if (uniqueRowIds.current.size >= MAX_SAMPLE_ROWS) {
          if (measurementTimer.current) {
            clearTimeout(measurementTimer.current);
            measurementTimer.current = null;
          }
          commitMeasurements();
          return;
        }
        uniqueRowIds.current.add(rowId);
      }

      const samples = widthSamplesRef.current.get(columnId) ?? [];
      if (samples.length < MAX_SAMPLE_ROWS) {
        samples.push(width);
        widthSamplesRef.current.set(columnId, samples);
      }

      columnWidthMap.current.set(columnId, Math.max(columnWidthMap.current.get(columnId) ?? 0, width));

      // Debounce the commit
      if (measurementTimer.current) clearTimeout(measurementTimer.current);
      measurementTimer.current = setTimeout(() => {
        measurementTimer.current = null;
        commitMeasurements();
      }, MEASUREMENT_DEBOUNCE_MS);
    },
    [commitMeasurements]
  );

  // Cleanup measurement timer on unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (measurementTimer.current) {
        clearTimeout(measurementTimer.current);
        measurementTimer.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (Array.isArray(data)) {
      setUseDataRetriever(false);
    } else {
      dataRetriever.current = data;
      setUseDataRetriever(true);
    }
  }, [data]);

  useSignalEffect(() => {
    setScrollingElement(scrollerElement.value);
  });

  useSignalEffect(() => {
    if (Array.isArray(data)) {
      let filteredData: JsonData = staticData.value;
      if (filterSignal.value.length > 0) {
        filteredData = filterData(filteredData, filterSignal.value);
      }

      if (sortSignal.value.length > 0) {
        filteredData = sortFunction(filteredData, sortSignal.value);
      }

      setFinalData(filteredData);
    }
  });

  useSignalEffect(() => {
    if (hasColumnsChanged.value) {
      setColumnsChangedTrigger((v) => !v);

      if (!isCommitting.current) {
        resetAutoSizeSession();
      }
    }

    if (idColumn.value && idColumn.value.field !== idFieldName) {
      setIdFieldName(idColumn.value.field);
    }
  });

  const idColumn = useComputed(() => getIdFieldColumn(columns.value));

  /**
   * This is needed since the user can change the order of the columns by dragging and dropping them. We need to
   * determine if the columns have actually changed and not just been reordered.
   * @returns {boolean} True if the columns are actually different.
   */
  const hasColumnsChanged = useComputed(() => {
    if (!previousColumns.current) {
      previousColumns.current = columns.value;
      return true;
    }

    if (previousColumns.current?.length !== columns.value.length) {
      return true;
    }

    // the logic here is that every column must have at least 1 match in the previous columns...this code has one flaw
    // if the columns have changed but happen to have the exact same field names then it will return false.
    const columnsChanged =
      columns.value.length > 0 &&
      !columns.value.every((column) => {
        const match = previousColumns.current?.some(
          (previousColumn) =>
            column.id === previousColumn.id &&
            column.field === previousColumn.field &&
            column.visible === previousColumn.visible
        );

        return match;
      });

    previousColumns.current = columns.value;

    return columnsChanged;
  });

  const addScrollerRefToApi = (scrollerRef: ContinuousScrollerRef) => {
    api.data.scrollerRef = scrollerRef;
    optionsContainerRef(scrollerRef.getContainerReference());
  };

  const addStaticContainerRefToApi = (container: HTMLDivElement) => {
    api.data.staticDataContainer = container;
    optionsContainerRef(container);
  };

  const dataLoadIndicator = useMemo(
    () => (
      <div className="flex w-full items-center justify-center absolute bottom-0 left-0 bg-mono-1 pt-2">
        <OdinIcon icon="RefreshCw" spin={true} />
        <span className="pl-2">{t('loading-data')}</span>
      </div>
    ),
    [t]
  );

  const renderCustomData = () => {
    if (customData && customData.length > 0) {
      return (
        <div>
          {customData.map((item, index) => {
            const key =
              idFieldName && item[idFieldName]
                ? `custom-row-${item[idFieldName]}`
                : `custom-row${index}-${customData.length}`;

            return (
              <DataGridDataRow
                key={key}
                columns={columns}
                selectedRows={selectedRows}
                highlightedRows={highlightedRows}
                idField={idColumn}
                rowIndex={index}
                data={item}
                isEven={index % 2 === 0}
                rowIndicator={rowIndicator}
                components={components}
                rowIsClickable={rowIsClickable}
                rowTemplate={rowTemplate}
                allowCellScrollBar={allowCellScrollBar}
                rowId={idFieldName && item[idFieldName] ? String(item[idFieldName]) : undefined}
                onMeasured={handleCellMeasured}
                isSizingActive={isSizingActive}
              />
            );
          })}
        </div>
      );
    }

    return null;
  };

  const renderContinuousScroller = () => {
    return (
      dataRetriever.current && (
        <ContinuousScroller
          key={`scroller_${columnChangedTrigger}`}
          dataRetriever={dataRetriever.current}
          scrollingElement={scrollingElement}
          dataLoadIndicator={dataLoadIndicator}
          filterSignal={filterSignal}
          sortSignal={sortSignal}
          sortFunction={sortFunction}
          searchLocation={filterLocation}
          pageSize={pageSize.value}
          minimumPageCount={minimumPageCount}
          stopPageRequestOnZeroPage={stopRequestsOnNoData}
          canUpdateData={true}
          useDataCache={true}
          idFieldName={idFieldName}
          className="overflow-visible!"
          ref={(ref) => {
            if (ref) addScrollerRefToApi(ref);
          }}
          isPaginated={isPaginated}
        >
          {(scrollerData, currentItemCount) => {
            return scrollerData.map((item, index) => (
              <DataGridDataRow
                key={`row${currentItemCount + index}`}
                columns={columns}
                selectedRows={selectedRows}
                highlightedRows={highlightedRows}
                idField={idColumn}
                rowIndex={index}
                data={item}
                isEven={index % 2 === 0}
                rowIndicator={rowIndicator}
                components={components}
                rowIsClickable={rowIsClickable}
                rowTemplate={rowTemplate}
                allowCellScrollBar={allowCellScrollBar}
                rowId={idFieldName && item[idFieldName] ? String(item[idFieldName]) : undefined}
                onMeasured={handleCellMeasured}
                isSizingActive={isSizingActive}
              />
            ));
          }}
        </ContinuousScroller>
      )
    );
  };

  const renderStaticData = () => {
    if (finalData && finalData.length > 0) {
      return (
        <div
          ref={(ref) => {
            if (ref) addStaticContainerRefToApi(ref);
          }}
        >
          {finalData.map((item, index) => {
            const key =
              idFieldName && item[idFieldName] && !Array.isArray(item[idFieldName])
                ? `row-${item[idFieldName]}`
                : `row${index}-${finalData.length}`;

            return (
              <DataGridDataRow
                key={key}
                columns={columns}
                selectedRows={selectedRows}
                highlightedRows={highlightedRows}
                idField={idColumn}
                rowIndex={index}
                data={item}
                isEven={index % 2 === 0}
                rowIndicator={rowIndicator}
                components={components}
                rowIsClickable={rowIsClickable}
                rowTemplate={rowTemplate}
                allowCellScrollBar={allowCellScrollBar}
                rowId={idFieldName && item[idFieldName] ? String(item[idFieldName]) : undefined}
                onMeasured={handleCellMeasured}
                isSizingActive={isSizingActive}
              />
            );
          })}
        </div>
      );
    } else {
      return (
        <div className="h-full w-full text-center">
          <div>{t('no-data', 'No Data')}</div>
        </div>
      );
    }
  };

  return (
    <>
      {renderCustomData()}
      {useDataRetriever ? renderContinuousScroller() : renderStaticData()}
    </>
  );
}
